import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCoachAthletes, getActiveAthleteProgram, getAthleteWorkoutLogs, getAthleteWeeklyVolume } from '../lib/database';
import { useSwipeBack } from '../hooks/useSwipeBack';
import './AthleteMonitoring.css';

function AthleteMonitoring() {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [athleteData, setAthleteData] = useState(null);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [weeklyVolume, setWeeklyVolume] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { handleTouchStart, handleTouchEnd } = useSwipeBack(() => navigate('/coach'));

  useEffect(() => {
    if (profile?.id && athleteId) {
      loadAthleteData();
      loadWeeklyVolume();
    }
  }, [profile, athleteId]);

  const loadAthleteData = async () => {
    try {
      setLoading(true);
      const athletes = await getCoachAthletes(profile.id);
      const athlete = athletes.find(a => a.athlete.id === athleteId);
      if (!athlete) {
        throw new Error('Atleta non trovato');
      }

      const activeProgram = await getActiveAthleteProgram(athleteId);
      const logs = await getAthleteWorkoutLogs(athleteId, 10);

      setAthleteData({
        id: athlete.id,
        athleteId: athlete.athlete.id,
        name: athlete.athlete.name,
        program: activeProgram?.program || null
      });
      setWorkoutLogs(logs);
    } catch (err) {
      console.error('Error loading athlete data:', err);
      setError('Errore nel caricamento dei dati atleta');
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklyVolume = async () => {
    try {
      const volume = await getAthleteWeeklyVolume(athleteId);
      setWeeklyVolume(volume);
    } catch (error) {
      console.error('Error loading weekly volume:', error);
      setWeeklyVolume({});
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('it-IT', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    }).format(date);
  };

  const formatVolume = (sets) => {
    return sets % 1 === 0 ? sets : (Math.round(sets * 10) / 10).toFixed(1);
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="am-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <header className="am-header">
          <div className="am-header-top">
            <button className="am-header-back" onClick={() => navigate('/coach')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="am-header-info">
              <div className="am-skeleton-line am-skeleton-line--short" style={{ height: 18, marginTop: 0 }} />
            </div>
          </div>
        </header>
        <main className="am-main">
          <div className="am-skeleton">
            {[1, 2, 3].map(i => (
              <div key={i} className="am-skeleton-card">
                <div className="am-skeleton-line am-skeleton-line--short" />
                <div className="am-skeleton-line am-skeleton-line--medium" />
                <div className="am-skeleton-line am-skeleton-line--long" />
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="am-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <header className="am-header">
          <div className="am-header-top">
            <button className="am-header-back" onClick={() => navigate('/coach')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </div>
        </header>
        <main className="am-main">
          <div className="am-empty">
            <div className="am-empty-icon">⚠️</div>
            <p className="am-empty-title">{error}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="am-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {/* Sticky Header */}
      <header className="am-header">
        <div className="am-header-top">
          <button className="am-header-back" onClick={() => navigate('/coach')} aria-label="Indietro">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="am-header-info">
            <h1 className="am-header-name">{athleteData.name}</h1>
            {athleteData.program && (
              <span className="am-header-program">{athleteData.program.name}</span>
            )}
          </div>
        </div>
      </header>

      <main className="am-main">
        {/* Volume Card */}
        {Object.keys(weeklyVolume).length > 0 && (
          <div className="am-volume-card">
            <h2 className="am-volume-title">Volume Settimanale</h2>
            <div className="am-volume-bars">
              {Object.entries(weeklyVolume)
                .sort(([, a], [, b]) => b - a)
                .map(([muscle, sets]) => {
                  const maxSets = Math.max(...Object.values(weeklyVolume));
                  const percentage = (sets / maxSets) * 100;

                  return (
                    <div key={muscle} className="am-volume-row">
                      <div className="am-volume-header">
                        <span className="am-volume-muscle">{muscle}</span>
                        <span className="am-volume-sets">{formatVolume(sets)} serie</span>
                      </div>
                      <div className="am-volume-track">
                        <div
                          className="am-volume-fill"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Last 10 Workouts */}
        <h2 className="am-section-title">Ultimi 10 Allenamenti</h2>

        {workoutLogs.length === 0 ? (
          <div className="am-empty">
            <div className="am-empty-icon">📊</div>
            <p className="am-empty-title">Nessun allenamento</p>
            <p className="am-empty-sub">L'atleta non ha ancora completato allenamenti</p>
          </div>
        ) : (
          <div className="am-log-list">
            {workoutLogs.map(log => (
              <div
                key={log.id}
                className="am-log-card"
                onClick={() => navigate(`/coach/athlete/${athleteId}/workout/${log.id}`)}
              >
                <span className="am-log-date">{formatDate(log.completed_at)}</span>

                {log.workout && (
                  <>
                    <span className="am-log-day">G{log.workout.day_number}</span>
                    <div className="am-log-info">
                      {log.workout.program && (
                        <div className="am-log-program">{log.workout.program.name}</div>
                      )}
                      <h3 className="am-log-name">{log.workout.name}</h3>
                    </div>
                  </>
                )}

                <div className="am-log-meta">
                  {log.duration_minutes && (
                    <span className="am-log-duration">{log.duration_minutes}'</span>
                  )}
                  <span className="am-log-chevron">›</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default AthleteMonitoring;
